/**
 * `<ab-slider>`: autoplaying scroll-snap carousel used by the Aromas de Brisa hero.
 *
 * Markup contract: a `[data-ab-track]` scroller holding `[data-ab-slide]` children,
 * optional `[data-ab-prev]` / `[data-ab-next]` buttons and a `[data-ab-dots]` container.
 * A slide may set `data-ab-duration` (seconds) to stay longer, e.g. a video slide.
 * Autoplay pauses on hover, focus, hidden tabs and reduced-motion preferences.
 */
class AbSlider extends HTMLElement {
  #controller = new AbortController();
  #timer = 0;
  #index = 0;
  #paused = false;
  #soundOn = false;

  connectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.track = this.querySelector('[data-ab-track]');
    this.slides = [...this.querySelectorAll('[data-ab-slide]')];
    if (!this.track || this.slides.length < 2) return;

    this.#buildDots();
    this.querySelector('[data-ab-prev]')?.addEventListener('click', () => this.go(this.#index - 1), { signal });
    this.querySelector('[data-ab-next]')?.addEventListener('click', () => this.go(this.#index + 1), { signal });

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#setIndex(this.slides.indexOf(/** @type {HTMLElement} */ (entry.target)));
        }
      },
      { root: this.track, threshold: 0.6 }
    );
    for (const slide of this.slides) observer.observe(slide);
    signal.addEventListener('abort', () => observer.disconnect());

    for (const button of this.querySelectorAll('[data-ab-sound]')) {
      button.addEventListener('click', () => this.#toggleSound(/** @type {HTMLButtonElement} */ (button)), { signal });
    }

    const pause = () => (this.#paused = true);
    const resume = () => (this.#paused = this.#soundOn);
    this.addEventListener('pointerenter', pause, { signal });
    this.addEventListener('pointerleave', resume, { signal });
    this.addEventListener('focusin', pause, { signal });
    this.addEventListener('focusout', resume, { signal });

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.seconds = Number(this.dataset.autoplay) || 0;
    if (this.seconds > 0 && !reduceMotion) this.#schedule();
  }

  disconnectedCallback() {
    this.#controller.abort();
    window.clearTimeout(this.#timer);
  }

  /** @param {HTMLButtonElement} button */
  #toggleSound(button) {
    const video = button.closest('[data-ab-slide]')?.querySelector('video');
    if (!video) return;
    this.#setSound(video.muted);
    if (!video.muted) {
      video.currentTime = 0;
      video.play().catch(() => {});
    }
  }

  /**
   * Turns sound on or off for every video slide and keeps the toggles in sync.
   * Autoplay stays paused while sound is on so the video is not cut off mid-way.
   * @param {boolean} on
   */
  #setSound(on) {
    this.#soundOn = on;
    this.#paused = on;
    for (const slide of this.slides) {
      const video = slide.querySelector('video');
      if (video) video.muted = !on || slide !== this.slides[this.#index];
      const button = slide.querySelector('[data-ab-sound]');
      if (!button) continue;
      button.setAttribute('aria-pressed', String(on));
      button.querySelector('[data-ab-sound-off]')?.toggleAttribute('hidden', on);
      button.querySelector('[data-ab-sound-on]')?.toggleAttribute('hidden', !on);
    }
  }

  /** Waits for the current slide's duration (its own `data-ab-duration` or the default), then advances. */
  #schedule() {
    window.clearTimeout(this.#timer);
    const slide = this.slides[this.#index];
    const seconds = Number(slide?.dataset.abDuration) || this.seconds;
    this.#timer = window.setTimeout(() => {
      if (!this.#paused && !document.hidden) this.go(this.#index + 1);
      this.#schedule();
    }, seconds * 1000);
  }

  /** @param {number} index */
  go(index) {
    const count = this.slides.length;
    const target = this.slides[((index % count) + count) % count];
    this.track.scrollTo({ left: target.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
  }

  #buildDots() {
    const dots = this.querySelector('[data-ab-dots]');
    if (!dots) return;
    dots.replaceChildren(
      ...this.slides.map((slide, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ab-slider__dot';
        dot.setAttribute('aria-label', `${this.dataset.dotLabel || 'Imagen'} ${i + 1}`);
        dot.addEventListener('click', () => this.go(i), { signal: this.#controller.signal });
        return dot;
      })
    );
    this.#setIndex(0);
  }

  /** @param {number} index */
  #setIndex(index) {
    if (index < 0) return;
    const changed = index !== this.#index;
    this.#index = index;
    this.querySelectorAll('.ab-slider__dot').forEach((dot, i) => dot.toggleAttribute('aria-current', i === index));

    if (!changed) return;
    // Leaving a slide with sound on silences it, so audio never plays off screen.
    if (this.#soundOn) this.#setSound(false);
    // Restart a video slide from the beginning each time it comes into view.
    const video = this.slides[index]?.querySelector('video');
    if (video) {
      video.currentTime = 0;
      video.play().catch(() => {});
    }
    if (this.seconds > 0 && this.#timer) this.#schedule();
  }
}

if (!customElements.get('ab-slider')) customElements.define('ab-slider', AbSlider);
